import {
  Badge,
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  SkeletonText,
  Spinner,
  useBreakpointValue,
  useToast,
} from "@chakra-ui/react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { Fragment, useState } from "react";
import { SUPPLIER_DASHBOARD_COLORS } from "../helpers";
import { useGetTopFiveInactiveSuppliers } from "../../../hooks/query/reports";
import { QUESTIONNAIRE_STATUSES, showErrorToast } from "../../../utils";
import { SUPPLIER_QUESTIONNAIRE_CATEGORIES } from "../../SupplierQuestionnaire/helpers";
import { supplierAdminDashboardYearsKeys } from "../SupplierAdminDashboard";
import { Card } from "../../../components/Card";
import { AppliedFilterIcon } from "./index";
import { H2 } from "components/text/headings";

function SuppliersToEngage({ filterCategory, categories }) {
  const { t } = useTranslation("translation", {
    keyPrefix: "supplierAdminDashboard",
  });
  const { t: globalTranslation } = useTranslation("translation");
  const toast = useToast();
  const [suppliersToEngage, setSuppliersToEngage] = useState([]);

  const { isFetching: isDataFetching } = useGetTopFiveInactiveSuppliers(filterCategory, {
    onSuccess: (data) => {
      if (data && Object.keys(data)?.length > 0) {
        const newData = Object.keys(data).map((supplierNameKey) => {
          return {
            name: supplierNameKey,
            dataStatus2021:
              data[supplierNameKey].find(
                (el) => Number(el.year) === 2021 && el.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
              )?.status ?? QUESTIONNAIRE_STATUSES.NEW,
            dataStatus2022:
              data[supplierNameKey].find(
                (el) => Number(el.year) === 2022 && el.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
              )?.status ?? QUESTIONNAIRE_STATUSES.NEW,
            dataStatus2023:
              data[supplierNameKey].find(
                (el) => Number(el.year) === 2023 && el.type !== SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION
              )?.status ?? QUESTIONNAIRE_STATUSES.NEW,
            decarbPlanStatus:
              data[supplierNameKey].find((el) => el.type === SUPPLIER_QUESTIONNAIRE_CATEGORIES.DECARBONIZATION)
                ?.status ?? QUESTIONNAIRE_STATUSES.NEW,
          };
        });
        setSuppliersToEngage(newData);
      } else {
        setSuppliersToEngage([]);
      }
    },
    onError: (err) => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    cacheTime: 0,
  });

  const getBackgroundColorByStatus = (status) => {
    switch (status) {
      case QUESTIONNAIRE_STATUSES.NEW:
        return SUPPLIER_DASHBOARD_COLORS.RED;
      case QUESTIONNAIRE_STATUSES.IN_PROGRESS:
        return SUPPLIER_DASHBOARD_COLORS.YELLOW;
      case QUESTIONNAIRE_STATUSES.FINALIZED:
        return SUPPLIER_DASHBOARD_COLORS.GREEN;
      default:
        return SUPPLIER_DASHBOARD_COLORS.RED;
    }
  };

  const headerDisplayType = useBreakpointValue({ base: "block", "2xl": "flex" });
  const legendColours = [
    {
      color: SUPPLIER_DASHBOARD_COLORS.GREEN,
      text: t("providedData"),
      legendTextColor: "white",
    },
    {
      color: SUPPLIER_DASHBOARD_COLORS.YELLOW,
      text: t("notFinished"),
      legendTextColor: "hsl(0,0%,43%)",
    },
    {
      color: SUPPLIER_DASHBOARD_COLORS.RED,
      text: t("notProvided"),
      legendTextColor: "white",
    },
  ];

  return (
    <Card padding={5} height={"100%"}>
      {isDataFetching ? (
        <SkeletonText mt="4" noOfLines={5} spacing="4" skeletonHeight="2" />
      ) : (
        <Flex flexFlow={"column"} gap={4} alignItems={isDataFetching ? "center" : "unset"}>
          <Flex justifyContent="space-between" alignItems="baseline" display={headerDisplayType}>
            <Flex alignItems="center">
              <Heading as="h2" size="md">
                {t("suppliersToEngage")}
              </Heading>
              {filterCategory !== "" && <AppliedFilterIcon filterCategory={filterCategory} categories={categories} />}
            </Flex>

            {suppliersToEngage.length > 0 ? (
              <Box>
                {legendColours.map((colorItem, index) => {
                  return (
                    <Badge
                      key={`legend-item-${colorItem.text}`}
                      backgroundColor={colorItem.color}
                      color={colorItem.legendTextColor}
                      marginRight={index < legendColours.length - 1 ? 2 : 0}
                    >
                      {colorItem.text}
                    </Badge>
                  );
                })}
              </Box>
            ) : null}
          </Flex>

          {isDataFetching ? (
            <Spinner size="lg" />
          ) : suppliersToEngage.length > 0 ? (
            <Grid templateColumns={`repeat(${3 + supplierAdminDashboardYearsKeys.length}, 1fr)`} gap={2}>
              {/* Header line - years and decarb labels */}
              <GridItem colSpan={2}></GridItem>
              {supplierAdminDashboardYearsKeys.map((yearKey) => {
                return (
                  <GridItem key={`year-label-${yearKey}`} fontWeight="bold" textAlign="center" fontSize="sm">
                    {yearKey}
                  </GridItem>
                );
              })}
              <GridItem fontWeight="bold" textAlign="center" fontSize="sm">
                {t("decarbPlan")}
              </GridItem>

              {suppliersToEngage.map((supplier) => {
                return (
                  <Fragment key={supplier.name}>
                    <GridItem
                      fontSize="sm"
                      as="span"
                      colSpan={2}
                      title={supplier.name}
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {supplier.name}
                    </GridItem>
                    {supplierAdminDashboardYearsKeys.map((yearKey) => {
                      return (
                        <GridItem
                          key={`supplier-to-engage-${yearKey}`}
                          backgroundColor={getBackgroundColorByStatus(supplier[`dataStatus${yearKey}`])}
                        ></GridItem>
                      );
                    })}
                    <GridItem
                      fontSize="lg"
                      backgroundColor={getBackgroundColorByStatus(supplier.decarbPlanStatus)}
                    ></GridItem>
                  </Fragment>
                );
              })}
            </Grid>
          ) : (
            <H2>{globalTranslation("noDataAvailable")}</H2>
          )}
        </Flex>
      )}
    </Card>
  );
}

export default SuppliersToEngage;
